import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { ChevronDown, ChevronRight, Pencil, Plus, Trash2 } from 'lucide-react';

import { useAppDispatch, useAppSelector } from '../../hooks';
import { catalogueReducer } from '../../store/catalogue/catalogue.reducer';
import { getCatalogueManufacturers, getSelectedManufacturer } from '../../store/catalogue/catalogue.selectors';
import { greyP5 } from '../../theme';
import DeleteConfirmModal from './DeleteConfirmModal';
import EditManufacturerModal from './EditManufacturerModal';

const { selectManufacturer, modifyManufacturer, removeManufacturer } = catalogueReducer.actions;

interface CatalogueManufacturerSidebarProps {
  onOpenAddManufacturerModal: () => void;
  canEdit?: boolean;
}

const CatalogueManufacturerSidebar: React.FC<CatalogueManufacturerSidebarProps> = ({
  onOpenAddManufacturerModal,
  canEdit = true,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const manufacturers = useAppSelector(getCatalogueManufacturers);
  const selectedManufacturer = useAppSelector(getSelectedManufacturer);
  const location = useLocation();
  const navigate = useNavigate();

  const [showAllManufacturers, setShowAllManufacturers] = useState(true);
  const selectedCategory = location.hash === '#favorites' ? 'favorites' : 'allManufacturers';

  useEffect(() => {
    if (location.hash === '#favorites' && selectedManufacturer) {
      dispatch(selectManufacturer(null));
    }
  }, [location.hash, selectedManufacturer, dispatch]);

  const handleCategoryClick = (categoryName: string) => {
    if (categoryName === 'favorites') {
      dispatch(selectManufacturer(null));
      navigate(`${location.pathname}${location.search}#favorites`, { replace: true });
    } else if (location.hash) {
      navigate(`${location.pathname}${location.search}`, { replace: true });
    }
  };
  const [hoveredManufacturerId, setHoveredManufacturerId] = useState<string | null>(null);
  const [editManufacturer, setEditManufacturer] = useState<{
    id: string;
    name: string;
    description?: string;
    logo?: string;
  } | null>(null);
  const [deleteManufacturerData, setDeleteManufacturerData] = useState<{ id: string; name: string } | null>(null);

  const handleManufacturerClick = (manufacturerId: string) => {
    if (selectedManufacturer?.id === manufacturerId) {
      dispatch(selectManufacturer(null));
    } else {
      dispatch(selectManufacturer(manufacturerId));
    }
  };

  const handleEditManufacturer = (
    e: React.MouseEvent,
    manufacturer: { id: string; name: string; description?: string; logo?: string },
  ) => {
    e.stopPropagation();
    setEditManufacturer(manufacturer);
  };

  const handleDeleteManufacturer = (e: React.MouseEvent, manufacturer: { id: string; name: string }) => {
    e.stopPropagation();
    setDeleteManufacturerData(manufacturer);
  };

  const handleUpdateManufacturer = (data: { name?: string; description?: string; logoBase64?: string }) => {
    if (editManufacturer) {
      dispatch(modifyManufacturer({ id: editManufacturer.id, data }));
    }
  };

  const handleConfirmDelete = () => {
    if (deleteManufacturerData) {
      dispatch(removeManufacturer(deleteManufacturerData.id));
    }
  };

  const secondaryCategories: Array<{ name: string; label: string; comingSoon?: React.ReactNode }> = [
    {
      name: 'favorites',
      label: t('catalogue.favorites'),
    },
  ];

  const creationActions = [
    { label: t('catalogue.newManufacturer'), icon: Plus, primary: true, onClick: () => onOpenAddManufacturerModal() },
  ];

  return (
    <nav
      className="flex-shrink-0 hidden md:flex flex-col overflow-y-auto border-r bg-black"
      style={{
        width: '240px',
        height: 'calc(100vh - 224px)',
        borderColor: greyP5,
      }}
      data-testid="catalogue-manufacturer-sidebar"
    >
      {/* All Manufacturers Header */}
      <button
        onClick={() => {
          setShowAllManufacturers(!showAllManufacturers);
          if (location.hash) {
            navigate(`${location.pathname}${location.search}`, { replace: true });
          }
        }}
        className={`flex-shrink-0 flex flex-row items-center justify-between w-full h-[44px] gap-2 text-left px-8 text-white font-medium
            border-b border-greyP5 transition-colors duration-200 text-xs ${
              selectedCategory === 'allManufacturers' ? 'bg-greyP5' : 'hover:bg-greyP5'
            }`}
      >
        <div className="flex items-center justify-between gap-[6px] w-[211px]">
          <span>{t('catalogue.allManufacturers')}</span>
          {showAllManufacturers ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </div>
      </button>

      <div className="bg-black">
        {/* Manufacturers List (collapsible) */}
        {showAllManufacturers && (
          <div
            className="flex flex-col items-start px-4 py-2"
            style={{
              gap: '2px',
            }}
          >
            {manufacturers.map((manufacturer) => (
              <div
                key={manufacturer.id}
                className="relative w-full"
                onMouseEnter={() => setHoveredManufacturerId(manufacturer.id)}
                onMouseLeave={() => setHoveredManufacturerId(null)}
              >
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => handleManufacturerClick(manufacturer.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleManufacturerClick(manufacturer.id);
                    }
                  }}
                  className={`flex items-center h-[40px] w-full text-sm transition-colors duration-200 cursor-pointer
                    ${selectedManufacturer?.id === manufacturer.id ? 'text-white' : 'text-greyP2 hover:text-white hover:bg-greyP4'}`}
                  style={
                    selectedManufacturer?.id === manufacturer.id
                      ? {
                          background: '#72A11A',
                          borderRadius: '6px',
                          padding: '10px 18px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          alignSelf: 'stretch',
                        }
                      : {
                          borderRadius: '6px',
                          padding: '10px 18px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          alignSelf: 'stretch',
                        }
                  }
                >
                  <span className="truncate flex-1 text-left" title={manufacturer.name}>
                    {manufacturer.name}
                  </span>
                  {canEdit && hoveredManufacturerId === manufacturer.id && (
                    <div className="flex items-center gap-1 mr-1">
                      <button
                        onClick={(e) => handleEditManufacturer(e, manufacturer)}
                        className="p-1 hover:bg-white/10 rounded"
                        title={t('common.edit')}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={(e) => handleDeleteManufacturer(e, manufacturer)}
                        className="p-1 hover:bg-white/10 rounded text-red-400"
                        title={t('common.delete')}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                  {manufacturer.series.length > 0 && <ChevronRight className="w-4 h-4 flex-shrink-0" />}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Secondary Categories (Favorites, Custom lights) */}
      {secondaryCategories.map((category) => (
        <button
          key={category.name}
          onClick={() => handleCategoryClick(category.name)}
          disabled={!!category.comingSoon}
          className={`flex-shrink-0 flex flex-row items-center justify-between w-full h-[44px] gap-2 text-left px-8 text-white font-medium
              disabled:cursor-not-allowed  
              border-b border-greyP5 transition-colors duration-200 text-xs ${
                selectedCategory === category.name ? 'bg-greyP5' : 'hover:bg-greyP5'
              }`}
        >
          <div className="flex items-center gap-2">
            <span>{category.label}</span>
          </div>
          {category.comingSoon}
        </button>
      ))}

      {/* Action Buttons — only for users with write access */}
      {canEdit && (
        <div className="flex-shrink-0 flex flex-col p-4 space-y-3">
          {creationActions.map((action) => {
            const IconComponent = action.icon;
            return (
              <button
                key={action.label}
                onClick={action.onClick}
                className={`flex items-center justify-between px-6 py-3 rounded-full font-medium transition-colors duration-200 text-xs ${
                  action.primary
                    ? 'bg-white text-black hover:bg-gray-100'
                    : 'border border-gray-600 text-white hover:bg-greyP5 hover:border-gray-500'
                }`}
                style={{
                  width: '80px',
                  height: '36px',
                  paddingTop: '10px',
                  paddingBottom: '10px',
                  paddingLeft: '18px',
                  paddingRight: '18px',
                  gap: '8px',
                }}
              >
                <IconComponent className="w-5 h-5" />
                {action.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Edit Manufacturer Modal */}
      <EditManufacturerModal
        isOpen={!!editManufacturer}
        onClose={() => setEditManufacturer(null)}
        onUpdate={handleUpdateManufacturer}
        manufacturer={editManufacturer}
      />

      {/* Delete Manufacturer Confirm Modal */}
      <DeleteConfirmModal
        isOpen={!!deleteManufacturerData}
        onClose={() => setDeleteManufacturerData(null)}
        onConfirm={handleConfirmDelete}
        title={t('catalogue.deleteManufacturerTitle')}
        itemName={deleteManufacturerData?.name || ''}
        itemType="manufacturer"
      />
    </nav>
  );
};

export default CatalogueManufacturerSidebar;
